import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";

import {
  DEFAULT_LANGUAGE,
  SITE_URL,
  getLangFromPathOrSearch,
  getLocalizedPath,
  getRouteKeyFromPath,
  languageLabels,
  pageSeo,
  stripLanguageFromPath,
  withLanguagePath,
} from "@/lib/i18nRoutes";
import { isAdminRoute } from "@/config/publicRoutes";
import { canonicalFor } from "@/lib/seo/canonical";
import { hreflangsFor } from "@/lib/seo/hreflang";
import { normalizeSeoPath } from "@/lib/seo/normalizeSeoPath";

const LEARNING_ARTICLE_RE = /^\/(?:[a-z]{2}\/)?learning-center\/[^/]+\/[^/?#]+/;
const ADSENSE_REVIEW_INDEXABLE_PATHS = new Set([
  "/",
  "/weather",
  "/marinas",
  "/learning-center",
  "/sailing-calculators",
  "/about",
  "/contact",
  "/privacy",
  "/terms",
  "/editorial-policy",
]);
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/icons/marstick-icon-512.png`;
const sanitizeSeoImage = (value) => /base44/i.test(String(value || "")) ? DEFAULT_SOCIAL_IMAGE : value;

function isRuntimeSeoPathIndexable(path = "/") {
  const clean = String(path || "/").toLowerCase().replace(/\/+$/, "") || "/";
  if (isAdminRoute(clean) || clean.startsWith("/admin")) return false;
  return ADSENSE_REVIEW_INDEXABLE_PATHS.has(clean);
}

function upsertMeta(selector, createTag, attributes) {
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement(createTag);
    document.head.appendChild(tag);
  }
  Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
}

function setStructuredData(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function removeSeoLinks() {
  document.querySelectorAll('link[rel="canonical"], link[data-i18n-seo], link[data-static-i18n-seo]').forEach(tag => tag.remove());
}

function setOgLocales(lang) {
  const localeMap = { en: "en_US", he: "he_IL", es: "es_ES", it: "it_IT", fr: "fr_FR", de: "de_DE", pt: "pt_PT", ar: "ar_AR", el: "el_GR", ru: "ru_RU", tr: "tr_TR", zh: "zh_CN" };
  document.querySelectorAll('meta[data-dynamic-og-locale="true"]').forEach(tag => tag.remove());
  upsertMeta('meta[property="og:locale"]', "meta", { property: "og:locale", content: localeMap[lang] || localeMap.en });
  Object.entries(localeMap)
    .filter(([code]) => code !== lang)
    .forEach(([, locale]) => {
      const tag = document.createElement("meta");
      tag.setAttribute("property", "og:locale:alternate");
      tag.setAttribute("content", locale);
      tag.setAttribute("data-dynamic-og-locale", "true");
      document.head.appendChild(tag);
    });
}

function appendSeoLink(rel, attributes) {
  const link = document.createElement("link");
  link.rel = rel;
  Object.entries(attributes).forEach(([key, value]) => value != null && link.setAttribute(key, value));
  link.setAttribute("data-i18n-seo", "true");
  document.head.appendChild(link);
}

function normalizePath(pathname) {
  const lowered = decodeURI(pathname || "/").toLowerCase();
  return lowered === "/" ? "/" : lowered.replace(/\/+$/, "");
}

function buildCanonical(pathname, search, override) {
  if (override) return override.replace(/\/+$/, "");
  const path = normalizePath(pathname);
  const params = new URLSearchParams(search || "");
  const page = params.get("page");
  const keepPage = page && /^\d+$/.test(page);
  const query = keepPage ? `?page=${page}` : "";
  return `${SITE_URL}${path === "/" ? "" : path}${query}`;
}

function isCanonicalSearch(search = "") {
  const params = new URLSearchParams(search || "");
  const keys = [...params.keys()];
  if (keys.length === 0) return true;
  return keys.length === 1 && /^\d+$/.test(params.get("page") || "");
}

function titleFromRoute(route = "/") {
  const parts = String(route || "/")
    .split("/")
    .filter(Boolean)
    .map((part) => decodeURIComponent(part).replace(/[-_]+/g, " "));
  if (!parts.length) return "Marstick";
  return parts.map((part) => part.replace(/\b\w/g, (char) => char.toUpperCase())).join(" — ");
}

const DIRECT_PATH_SEO = {
  "/marinas-near-me": {
    en: {
      title: "Marinas Near Me: Fuel, Berths & Sailor Reports | Marstick",
      description: "Find marinas near you with fuel, berth planning, weather checks, facilities and sailor reports before arrival.",
    },
  },
  "/weather/beaufort-guide": {
    en: {
      title: "Beaufort Scale: Wind Speed, Sea State & Safety | Marstick",
      description: "Use this Beaufort scale guide to read wind speed, sea state, sail handling risk and safer departure decisions.",
    },
  },
};

function selectSeo({ route, routeKey, lang, seoMeta }) {
  const hardcoded = pageSeo[routeKey]?.[lang] || pageSeo[routeKey]?.[DEFAULT_LANGUAGE];
  const fallbackLabel = titleFromRoute(route);
  const fallbackTitle = `${fallbackLabel} | Marstick`;
  const fallbackDescription = `Practical maritime guidance for ${fallbackLabel.toLowerCase()}, with sailing safety, weather, navigation, marina, and voyage planning context.`;
  const hasRouteSpecificPath = String(route || "/").split("/").filter(Boolean).length > 1;
  const directSeo = DIRECT_PATH_SEO[route]?.[lang] || DIRECT_PATH_SEO[route]?.[DEFAULT_LANGUAGE];
  if (directSeo) {
    return {
      title: directSeo.title,
      description: directSeo.description,
      ogTitle: directSeo.title,
      ogDescription: directSeo.description,
      ogImage: undefined,
      canonicalOverride: undefined,
      noIndex: false,
    };
  }

  if (routeKey === "home") {
    return {
      title: hardcoded?.title || "Marstick — Maritime navigation, weather & learning for sailors",
      description: hardcoded?.description || "Marstick helps sailors plan safer voyages with marine weather, nautical tools, marina guides, sailing education, and premium voyage planning.",
      ogTitle: hardcoded?.title || "Marstick — Maritime navigation, weather & learning for sailors",
      ogDescription: hardcoded?.description || "Marstick helps sailors plan safer voyages with marine weather, nautical tools, marina guides, sailing education, and premium voyage planning.",
      ogImage: undefined,
      canonicalOverride: undefined,
      noIndex: false,
    };
  }

  const langMatch = seoMeta.find(item => item.route === route && item.language === lang);
  const defaultMatch = seoMeta.find(item => item.route === route && item.language === "default");
  const entity = langMatch || defaultMatch;
  const title = entity?.title || (hasRouteSpecificPath ? fallbackTitle : hardcoded?.title) || fallbackTitle;
  const description = entity?.description || (hasRouteSpecificPath ? fallbackDescription : hardcoded?.description) || fallbackDescription;
  return {
    title,
    description,
    ogTitle: entity?.ogTitle || entity?.title || title,
    ogDescription: entity?.ogDescription || entity?.description || description,
    ogImage: entity?.ogImage,
    canonicalOverride: entity?.canonicalOverride,
    noIndex: entity?.noIndex === true,
  };
}

export default function SeoManager() {
  const location = useLocation();
  const navigate = useNavigate();
  const [seoMeta, setSeoMeta] = React.useState([]);

  React.useEffect(() => {
    if (import.meta.env.DEV) return;

    let mounted = true;
    const loadSeoMeta = () => {
      base44.entities.SeoMeta.list("route", 500)
        .then(records => mounted && setSeoMeta(records || []))
        .catch(() => mounted && setSeoMeta([]));
    };
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(loadSeoMeta, { timeout: 3500 }) : window.setTimeout(loadSeoMeta, 2200);
    return () => {
      mounted = false;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  React.useEffect(() => {
    window.prerenderReady = false;

    const path = normalizePath(location.pathname);
    const lang = getLangFromPathOrSearch(path, location.search);
    const normalizedSeoPath = normalizeSeoPath(path);
    const detectedRouteKey = normalizedSeoPath.routeKey || getRouteKeyFromPath(path);
    const cleanPath = stripLanguageFromPath(path);
    const routeKey = detectedRouteKey || (cleanPath === "/" ? "home" : null);
    const registryPath = normalizedSeoPath.path;
    const admin = isAdminRoute(registryPath) || isAdminRoute(cleanPath) || isAdminRoute(path) || cleanPath.startsWith("/admin");
    const privateApp = /^(\/dashboard|\/logbook|\/boats|\/trips|\/settings|\/billing|\/profile|\/maintenance|\/academy|\/premium-operations|\/metrics|\/offline|\/onboarding)(\/|$)/.test(cleanPath);
    const premium = privateApp;
    const isLearningArticle = LEARNING_ARTICLE_RE.test(registryPath);
    const routeForMeta = isLearningArticle ? "/learning" : registryPath;
    const seo = selectSeo({ route: routeForMeta, routeKey, lang, seoMeta });
    const registryCanonical = canonicalFor({ path, lang });
    const registryIndexed = !!registryCanonical && isRuntimeSeoPathIndexable(registryPath);
    const canonicalSearch = isCanonicalSearch(location.search);
    const pageParam = new URLSearchParams(location.search || "").get("page");
    const canonicalBase = seo.canonicalOverride
      ? seo.canonicalOverride.replace(/\/+$/, "")
      : (registryCanonical || buildCanonical(path, location.search));
    const canonicalUrl = registryCanonical && pageParam && /^\d+$/.test(pageParam)
      ? `${canonicalBase}?page=${pageParam}`
      : canonicalBase;
    const hasFallbackMeta = !!seo.title?.trim() && !!seo.description?.trim() && !!registryCanonical;
    const hasLocalizedMeta = lang === DEFAULT_LANGUAGE || !!pageSeo[routeKey]?.[lang] || !!seoMeta.find(item => item.route === routeForMeta && item.language === lang && item.title?.trim() && item.description?.trim()) || hasFallbackMeta;
    const host = window.location.hostname.toLowerCase();
    const isMarstickHost = host === "marstick.com" || host === "www.marstick.com";
    const shouldIndex = isMarstickHost && lang === DEFAULT_LANGUAGE && canonicalSearch && !admin && !premium && !seo.noIndex && hasLocalizedMeta && registryIndexed;
    const shouldEmitCanonical = !admin && !premium && hasLocalizedMeta && !!canonicalUrl;
    const robotsContent = shouldIndex ? "index, follow" : "noindex, nofollow";
    if (import.meta.env.DEV && !hasLocalizedMeta) console.warn(`[SEO] No localized meta for ${path}:${lang}`);

    document.documentElement.lang = lang;
    document.documentElement.dir = languageLabels[lang]?.dir || "ltr";
    document.documentElement.classList.toggle("rtl", languageLabels[lang]?.dir === "rtl");
    if (document.body) document.body.classList.toggle("rtl", languageLabels[lang]?.dir === "rtl");

    if (!isLearningArticle) {
      document.title = seo.title;
      upsertMeta('meta[name="description"]', "meta", { name: "description", content: seo.description });
    }

    upsertMeta('meta[name="robots"]', "meta", { name: "robots", content: shouldIndex ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" : robotsContent });
    upsertMeta('meta[name="googlebot"]', "meta", { name: "googlebot", content: shouldIndex ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" : robotsContent });
    upsertMeta('meta[name="application-name"]', "meta", { name: "application-name", content: "Marstick" });
    upsertMeta('meta[name="apple-mobile-web-app-title"]', "meta", { name: "apple-mobile-web-app-title", content: "Marstick" });
    upsertMeta('meta[name="author"]', "meta", { name: "author", content: "Marstick" });
    upsertMeta('meta[name="publisher"]', "meta", { name: "publisher", content: "Marstick" });
    upsertMeta('meta[itemprop="name"]', "meta", { itemprop: "name", content: "Marstick" });
    upsertMeta('meta[property="og:site_name"]', "meta", { property: "og:site_name", content: "Marstick" });
    upsertMeta('meta[property="og:type"]', "meta", { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:title"]', "meta", { property: "og:title", content: seo.ogTitle });
    upsertMeta('meta[property="og:description"]', "meta", { property: "og:description", content: seo.ogDescription });
    upsertMeta('meta[property="og:url"]', "meta", { property: "og:url", content: canonicalUrl });
    setOgLocales(lang);
    const socialImage = sanitizeSeoImage(seo.ogImage) || DEFAULT_SOCIAL_IMAGE;
    upsertMeta('meta[property="og:image"]', "meta", { property: "og:image", content: socialImage });
    upsertMeta('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: socialImage });
    upsertMeta('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: seo.ogTitle });
    upsertMeta('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: seo.ogDescription });
    upsertMeta('meta[name="twitter:url"]', "meta", { name: "twitter:url", content: canonicalUrl });

    removeSeoLinks();
    if (shouldEmitCanonical) {
      appendSeoLink("canonical", { href: canonicalUrl });
      if (import.meta.env.DEV && !canonicalUrl.startsWith(SITE_URL)) {
        console.warn(`[SEO] Canonical must start with ${SITE_URL}: ${canonicalUrl}`);
      }
    }

    if (shouldIndex) {
      hreflangsFor({ path, currentLang: lang }).forEach(({ lang: altLang, href }) => {
        appendSeoLink("alternate", { hreflang: altLang, href });
      });
      appendSeoLink("alternate", { type: "application/rss+xml", title: "Marstick RSS Feed", href: `${SITE_URL}/rss.xml` });
      appendSeoLink("alternate", { type: "application/atom+xml", title: "Marstick Atom Feed", href: `${SITE_URL}/atom.xml` });
    }

    setStructuredData("schema-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Marstick",
      legalName: "Marstick",
      url: SITE_URL,
      logo: `${SITE_URL}/apple-touch-icon.png`,
      sameAs: [SITE_URL],
    });

    setStructuredData("schema-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "Marstick",
      alternateName: "Marstick Maritime Command Center",
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: lang,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}${getLocalizedPath("learningCenter", lang)}?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });

    setStructuredData("schema-webpage", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seo.title,
      description: seo.description,
      url: canonicalUrl,
      inLanguage: lang,
      isPartOf: { "@id": `${SITE_URL}/#website`, "@type": "WebSite", name: "Marstick", url: SITE_URL },
      publisher: { "@id": `${SITE_URL}/#organization` },
    });

    requestAnimationFrame(() => { window.prerenderReady = true; });
  }, [location.pathname, location.search, seoMeta]);

  React.useEffect(() => {
    const onClick = (event) => {
      const anchor = event.target.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || anchor.hasAttribute("download") || href.startsWith("http") || href.startsWith("blob:") || href.startsWith("data:") || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (href.startsWith("/admin")) return;
      const lang = getLangFromPathOrSearch(location.pathname, location.search);
      const nextPath = withLanguagePath(href, lang);
      if (nextPath !== href) {
        event.preventDefault();
        navigate(nextPath);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname, location.search, navigate]);

  return null;
}